// While changelist can't be reached, only one tab tries again: the tab
// holding the `changelist:reconnect` Web Lock leads (runs its retries); the
// others follow, and hear on a BroadcastChannel when the leader got
// through. When the leader's tab closes or recovers, the next tab in line
// leads. Without Web Locks every tab leads.

const LOCK = 'changelist:reconnect';
const CHANNEL = 'changelist:reconnect';
const UP = 'up';

export interface ReconnectLocks {
  request(name: string, opts: { signal: AbortSignal }, cb: () => Promise<void>): Promise<unknown>;
}

export interface ReconnectChannel {
  postMessage(data: unknown): void;
  addEventListener(type: 'message', fn: (m: MessageEvent) => void): void;
  close(): void;
}

export interface Reconnect {
  /** This tab got through: tell the others. */
  recovered(): void;
  /** Stop leading, or leave the line. */
  stop(): void;
}

function browserDeps(): { locks: ReconnectLocks | undefined; channel: ReconnectChannel | undefined } {
  const locks = typeof navigator !== 'undefined' ? (navigator as { locks?: ReconnectLocks }).locks : undefined;
  const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : undefined;
  return { locks, channel };
}

/**
 * Takes part in reconnecting. `lead` runs while this tab leads and should
 * resolve once it stops trying (recovered, or stopped); `onUp` runs when
 * another tab got through; `onRole` says whether this tab leads or
 * follows, whenever that changes.
 */
export function coordinateReconnect(
  opts: { lead: () => Promise<void>; onUp: () => void; onRole: (role: 'leader' | 'follower') => void },
  deps = browserDeps(),
): Reconnect {
  const { locks, channel } = deps;
  const abort = new AbortController();
  channel?.addEventListener('message', (m) => {
    if (m.data === UP && !abort.signal.aborted) opts.onUp();
  });
  const lead = async () => {
    if (abort.signal.aborted) return;
    opts.onRole('leader');
    await opts.lead();
  };
  if (!locks) void lead();
  else {
    opts.onRole('follower');
    locks.request(LOCK, { signal: abort.signal }, lead).catch(() => {
      /* aborted while waiting in line */
    });
  }
  return {
    recovered: () => channel?.postMessage(UP),
    stop: () => {
      abort.abort();
      channel?.close();
    },
  };
}
